(() => {
  if (window.NeyaBundleEngineV2Loaded) return;
  window.NeyaBundleEngineV2Loaded = true;
  window.NeyaBundleEngineVersion = '2.0.0';

  class NeyaBundleBase extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
      this.cartBehavior = this.dataset.cartBehavior || 'external';
      this.cart = this.cartBehavior === 'native'
        ? document.querySelector('cart-drawer')
        : null;
    }

    formatMoney(cents) {
      const value = Number(cents || 0);

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const formatted = window.Shopify.formatMoney(value, this.moneyFormat);
        const wrapper = document.createElement('span');
        wrapper.innerHTML = formatted;
        return wrapper.textContent;
      }

      const currency = window.Shopify?.currency?.active || 'EUR';
      const locale = document.documentElement.lang || 'fr-FR';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        }).format(value / 100);
      } catch (error) {
        return `${(value / 100).toFixed(2).replace('.', ',')} €`;
      }
    }

    getCartSections() {
      if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return [];
      return this.cart.getSectionsToRender().map((section) => section.id);
    }

    async addItems(items, bundleName, button, message) {
      if (!items.length || !window.routes?.cart_add_url) return;

      const groupedItems = new Map();
      const bundleKey = `neya-${Date.now()}`;
      const cartAddUrl = window.routes.cart_add_url.endsWith('.js')
        ? window.routes.cart_add_url
        : `${window.routes.cart_add_url}.js`;

      items.forEach((item) => {
        const key = String(item.id);
        const existing = groupedItems.get(key);

        if (existing) {
          existing.quantity += 1;
        } else {
          groupedItems.set(key, {
            id: Number(item.id),
            quantity: 1,
            properties: {
              '_Offre Neya Nature': bundleName,
              '_Identifiant du trio': bundleKey,
            },
          });
        }
      });

      const payload = { items: Array.from(groupedItems.values()) };
      const sections = this.getCartSections();

      if (sections.length) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }

      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      message.textContent = '';

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const responseText = await response.text();
        let result = {};

        if (responseText.trim()) {
          try {
            result = JSON.parse(responseText);
          } catch (parseError) {
            /*
             * Certaines configurations Shopify ou applications renvoient une
             * page HTML après un ajout pourtant réussi. On ne transforme pas
             * cette réponse valide en faux message d'erreur.
             */
            if (!response.ok) {
              throw new Error('Shopify n’a pas pu ajouter ce trio au panier.');
            }
          }
        }

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Impossible d’ajouter ce trio au panier.');
        }

        /*
         * Corner Cart intercepte déjà l'appel /cart/add.js et gère sa propre
         * ouverture. Dans ce mode, il ne faut surtout pas ouvrir Dawn aussi.
         */
        if (this.cartBehavior === 'external') {
          return true;
        }

        if (
          this.cartBehavior === 'native'
          && this.cart
          && result.sections
          && typeof this.cart.renderContents === 'function'
        ) {
          /*
           * Dawn conserve la classe is-empty après un ajout multiple. Si elle
           * n'est pas retirée avant open(), le drawer masque ses lignes et
           * trapFocus reçoit un élément null.
           */
          this.cart.classList.remove('is-empty');
          this.cart.setActiveElement?.(button);
          this.cart.renderContents(result);
          return true;
        }

        /*
         * Repli sûr si le panier natif est demandé mais n'a pas renvoyé les
         * sections attendues, ou si la redirection a été choisie.
         */
        window.location.assign(window.routes.cart_url || '/cart');
        return true;
      } catch (error) {
        message.textContent = error.message || 'Une erreur est survenue. Veuillez réessayer.';
        return false;
      } finally {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    }
  }

  class NeyaCuratedBundles extends NeyaBundleBase {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      super.connectedCallback();

      this.querySelectorAll('[data-neya-add-bundle]').forEach((button) => {
        button.addEventListener('click', () => this.addBundle(button));
      });
    }

    async addBundle(button) {
      const card = button.closest('[data-neya-bundle-card]');
      if (!card) return;

      const message = card.querySelector('[data-neya-message]');
      const products = Array.from(card.querySelectorAll('[data-neya-bundle-product]'));
      const items = products
        .map((product) => ({ id: product.dataset.variantId }))
        .filter((item) => item.id);

      if (items.length !== 3) {
        message.textContent = 'Ce trio doit contenir trois produits disponibles.';
        return;
      }

      await this.addItems(items, card.dataset.bundleName || 'Trio Neya Nature', button, message);
    }
  }

  class NeyaBundleBuilder extends NeyaBundleBase {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      super.connectedCallback();
      this.selected = [];
      this.limit = Number(this.dataset.selectionLimit || 3);
      this.cards = Array.from(this.querySelectorAll('[data-neya-product-choice]'));
      this.count = this.querySelector('[data-neya-count]');
      this.progress = this.querySelector('[data-neya-progress]');
      this.summaryButton = this.querySelector('[data-neya-add-selection]');
      this.message = this.querySelector('[data-neya-message]');
      this.slotContainer = this.querySelector('[data-neya-slots]');

      this.cards.forEach((card) => {
        card.querySelector('[data-neya-toggle]')?.addEventListener('click', () => this.toggleCard(card));

        card.querySelector('[data-neya-variant]')?.addEventListener('change', (event) => {
          this.updateVariant(card, event.currentTarget);
        });
      });

      this.querySelectorAll('[data-neya-filter]').forEach((filter) => {
        filter.addEventListener('click', () => this.applyFilter(filter));
      });

      this.summaryButton?.addEventListener('click', () => this.addSelection());
      this.render();
    }

    updateVariant(card, select) {
      const option = select.options[select.selectedIndex];
      card.dataset.variantId = option.value;
      card.dataset.price = option.dataset.price;
      card.dataset.variantTitle = option.dataset.title || '';
      card.querySelector('[data-neya-card-price]').textContent = this.formatMoney(option.dataset.price);

      if (card.classList.contains('is-selected')) {
        const selectedItem = this.selected.find((item) => item.productId === card.dataset.productId);
        if (selectedItem) Object.assign(selectedItem, this.getCardData(card));
        this.render();
      }
    }

    getCardData(card) {
      return {
        productId: card.dataset.productId,
        id: card.dataset.variantId,
        title: card.dataset.title,
        variantTitle: card.dataset.variantTitle || '',
        price: Number(card.dataset.price || 0),
        image: card.dataset.image || '',
      };
    }

    toggleCard(card) {
      const productId = card.dataset.productId;
      const selectedIndex = this.selected.findIndex((item) => item.productId === productId);

      if (selectedIndex >= 0) {
        this.selected.splice(selectedIndex, 1);
        card.classList.remove('is-selected');
        card.querySelector('[data-neya-toggle]').textContent = this.dataset.addLabel || 'Choisir';
      } else {
        if (this.selected.length >= this.limit) {
          this.message.textContent = `Votre trio est déjà complet. Retirez un produit pour le remplacer.`;
          return;
        }

        this.selected.push(this.getCardData(card));
        card.classList.add('is-selected');
        card.querySelector('[data-neya-toggle]').textContent = this.dataset.removeLabel || 'Retirer';
        this.message.textContent = '';
      }

      this.render();
    }

    applyFilter(activeFilter) {
      const category = activeFilter.dataset.neyaFilter;

      this.querySelectorAll('[data-neya-filter]').forEach((filter) => {
        filter.setAttribute('aria-pressed', String(filter === activeFilter));
      });

      this.cards.forEach((card) => {
        card.hidden = category !== 'all' && card.dataset.category !== category;
      });
    }

    render() {
      const selectedCount = this.selected.length;
      const complete = selectedCount === this.limit;

      this.count.textContent = `${selectedCount} produit${selectedCount > 1 ? 's' : ''} sur ${this.limit}`;
      this.progress.style.setProperty('--neya-progress', `${(selectedCount / this.limit) * 100}%`);

      this.cards.forEach((card) => {
        const selected = card.classList.contains('is-selected');
        card.classList.toggle('is-locked', complete && !selected);
        card.querySelector('[data-neya-toggle]').setAttribute('aria-pressed', String(selected));
      });

      this.renderSlots();
      this.renderTotals();

      this.summaryButton.disabled = !complete;
      this.summaryButton.setAttribute('aria-disabled', String(!complete));
    }

    renderSlots() {
      const slots = Array.from(this.slotContainer.children);

      slots.forEach((slot, index) => {
        const item = this.selected[index];
        const visual = slot.querySelector('[data-neya-slot-visual]');
        const name = slot.querySelector('[data-neya-slot-name]');
        const price = slot.querySelector('[data-neya-slot-price]');

        if (!item) {
          slot.classList.remove('is-filled');
          visual.className = 'neya-builder__slot-index';
          visual.innerHTML = String(index + 1);
          name.textContent = 'Choisissez un produit';
          price.textContent = '';
          return;
        }

        slot.classList.add('is-filled');
        visual.className = 'neya-builder__slot-image';
        visual.innerHTML = item.image
          ? `<img src="${this.escapeHtml(item.image)}" alt="" width="80" height="80" loading="lazy">`
          : String(index + 1);
        name.textContent = item.variantTitle && item.variantTitle !== 'Default Title'
          ? `${item.title} — ${item.variantTitle}`
          : item.title;
        price.textContent = this.formatMoney(item.price);
      });
    }

    renderTotals() {
      const total = this.selected.reduce((sum, item) => sum + item.price, 0);
      const saving = this.selected.length === this.limit
        ? Math.min(...this.selected.map((item) => item.price))
        : 0;
      const promo = total - saving;

      this.querySelector('[data-neya-total]').textContent = this.formatMoney(total);
      this.querySelector('[data-neya-saving]').textContent = `− ${this.formatMoney(saving)}`;
      this.querySelector('[data-neya-promo]').textContent = this.formatMoney(promo);
    }

    async addSelection() {
      if (this.selected.length !== this.limit) {
        this.message.textContent = `Choisissez ${this.limit} produits pour compléter votre trio.`;
        return;
      }

      await this.addItems(
        this.selected.map((item) => ({ id: item.id })),
        this.dataset.bundleName || 'Trio personnalisé Neya Nature',
        this.summaryButton,
        this.message
      );
    }

    escapeHtml(value) {
      const element = document.createElement('div');
      element.textContent = value;
      return element.innerHTML;
    }
  }

  if (!customElements.get('neya-curated-bundles')) {
    customElements.define('neya-curated-bundles', NeyaCuratedBundles);
  }

  if (!customElements.get('neya-bundle-builder')) {
    customElements.define('neya-bundle-builder', NeyaBundleBuilder);
  }
})();

